import type { Immutable } from "immer";
import React from "react";

import type { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { DistanceToFlagshipStore } from "../apps/retail-locator/DistanceToFlagshipStore";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/flagship/store_front.html`` (FlagshipPage ``StoreFrontBlock``, a
 * StaticBlock).
 *
 * The whole ``.intro`` hero moves into React. It is built entirely from
 * PAGE-level data threaded as a side-channel from the include_react shim:
 * ``storefront_url`` (the ``page.storefront`` ``original`` rendition, resolved
 * server-side and used as the ``background-image``), ``page_intro`` / ``title``
 * (both raw-HTML ``|safe`` injects -> ``dangerouslySetInnerHTML``) and
 * ``shopping_district`` (plain text). The ``intro--overlay`` modifier and the
 * inner ``.intro__wrapper`` both gate on ``page_intro``, exactly as the legacy
 * ``{% if page.page_intro %}`` did.
 *
 * ``DistanceToFlagshipStore`` is the legacy inline interactive island
 * (``{% include_react "...DistanceToFlagshipStoreTemplate" %}``, a Redux
 * ``useAppSelector`` "miles away / See Closest Location" readout). It is rendered
 * as a REAL React child here (NOT baked into a string, which would render but
 * never hydrate). CRITICAL DOM parity: in the legacy markup the island sat
 * inside the ``<p class="intro__subtext2">`` after ``{{ shopping_district }} /``,
 * but ``include_react`` emits a block ``<div>``, so the browser SPLIT the
 * paragraph -- the baseline render is ``<p>… / </p>`` then the island ``<div>``
 * then an empty ``<p></p>``. We reproduce that split (three siblings) verbatim;
 * the wrapping ``<div>`` doubles as the ``useAppSelector`` SSR boundary.
 */
export const Template = (
    props: Immutable<templates.FlagshipStoreFrontBlockTemplate>,
): React.JSX.Element => {
    const hasIntro = !!props.page_intro;
    const sectionClass = concatClassNames([
        "intro",
        "l-full-width",
        hasIntro ? "intro--overlay" : "",
    ]);
    return (
        <section
            className={sectionClass}
            style={{ backgroundImage: `url(${props.storefront_url})` }}
        >
            {hasIntro ? (
                <div className="intro__wrapper l-capped-width">
                    <div className="intro__content">
                        <h1
                            className="intro__headline"
                            dangerouslySetInnerHTML={{
                                __html: props.page_intro,
                            }}
                        />
                        <h2
                            className="intro__subtext1"
                            dangerouslySetInnerHTML={{ __html: props.title }}
                        />
                        <p className="intro__subtext2 type_x0point875">
                            {props.shopping_district} /{" "}
                        </p>
                        <div>
                            <DistanceToFlagshipStore />
                        </div>
                        <p></p>
                    </div>
                </div>
            ) : null}
        </section>
    );
};
